<template>
  <div :class="[{ 'owc-media-gallery__theme-dark': isDarkMode }, 'owc-media-gallery__wrapper']">
    <ValidationBoxWrapper>
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <section
      :class="[{ 'owc-media-gallery_isEditMode': isEditMode }, 'owc-media-gallery__container']"
      ref="mediaGalleryContainer"
    >
      <SlotContainer
        class="owc-media-gallery"
        :class="layoutClass"
        elementDOM="ul"
        refName="slot-media-gallery"
      >
        <slot></slot>
      </SlotContainer>
      <transition name="owc-gallery-overlay-fade-in-out">
        <template v-if="isOverlayVisible">
          <MediaGalleryOverlay
            :isDarkMode="isDarkMode"
            :galleryItems="currentGalleryItems"
            @close="isOverlayVisible = false"
            :overlayPinchLabel="pinchLabel"
            :galleryItemIndex="currentGalleryItemIndex"
          />
        </template>
      </transition>
      <div v-if="footnotes" class="owc-media-gallery__footnotes">
        <globalelem-footnote-component
          :module-id="moduleId"
          :theme="isDarkMode ? 'dark' : 'light'"
        ></globalelem-footnote-component>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
/**
 * `MediaGallery` - The Media Gallery module for PI is a great way to illustrate the page with images in a dynamic grid layout.
 *
 *  This component has a specific requirement that a row height must be the same of the width of 8 columns, and columns
 *  do not have a fixed width. As such, this value must be computed on runtime.
 *  A resize observer is used, as it does not cause layout thrashing (as opposed to getClientRect() and the likes)
 *  and a css custom property is set "--mediaGalleryWidth". While webpack could be leveraged to export the css
 *  values into the javascript code, this would not happen at runtime, and gutters are a variable here that makes
 *  having all the computation done by calc the preferred option.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import {
  MEDIA_GALLERY_DEBOUNCE_TIME,
  MEDIA_GALLERY_ITEM_ACTION_EVENT_NAME,
  MEDIA_GALLERY_ITEM_TRACKING_EVENT_NAME,
} from './mediaGallery.config';
import { debounce } from '@owc-utils/Events';
import MediaGalleryOverlay from './MediaGalleryOverlay/MediaGalleryOverlay.vue';
import { getComponentStateConnection, subscribeComponentState } from '@owc-utils/Store/owcComponent';
import {
  MediaGalleryData,
  MediaGalleryMethods,
  MediaGalleryComputed,
  Payload,
  MediaGalleryItemComponent,
} from './MediaGallery';
import { Props, State } from '@/packages/_types/ComponentInitializeDispatcher';
import { parseTree, Tree } from './MediaGalleryUtils';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';

let isItemHeightSet = false;

export default ComponentInitializeDispatcher.extend<
  MediaGalleryData,
  MediaGalleryMethods,
  MediaGalleryComputed,
  Partial<Props>
>({
  name: 'owc-media-gallery',
  components: {
    SlotContainer,
    MediaGalleryOverlay,
    ValidationBoxWrapper,
  },
  async mounted() {
    const id = this.getComponentId();

    this.resizeEventHandler();
    if (this.isBuildWc) {
      this.$nextTick(() => {
        const tree: Tree = parseTree(this.getHost());
        const listener = (state: State): void => {
          const innerItems: string[] = [];
          const items = tree.map(({ childComponents, id: itemId }) => {
            const item = state.find(component => component.id === itemId);
            const inner = childComponents?.[0];
            const found = state.find(component => component.id === inner?.id);
            if (found) {
              innerItems.push(found.id);
            }
            return {
              itemId,
              itemJson: item?.json || {},
              id: found?.id,
              json: found?.json,
            } as MediaGalleryItemComponent;
          });
          this.currentGalleryItems = items;
          this.setItemHeight(innerItems);
        };
        subscribeComponentState(id, listener);
      });
    }
    this.resizeEventHandler();

    const resizeObserver = new ResizeObserver(
      debounce(MEDIA_GALLERY_DEBOUNCE_TIME, this.resizeEventHandler, false),
    );

    resizeObserver.observe(this.$refs.mediaGalleryContainer as HTMLElement);
    this.$el.addEventListener(MEDIA_GALLERY_ITEM_ACTION_EVENT_NAME, this.openOverlay as EventListener);
    this.$el.addEventListener(MEDIA_GALLERY_ITEM_TRACKING_EVENT_NAME, this.handleTracking as EventListener);
  },
  data() {
    return {
      isOverlayVisible: false,
      currentGalleryItems: [],
      currentGalleryItemIndex: 0,
      currentGridWidth: 0,
      isTrackingElementDispached: false,
    };
  },
  computed: {
    layoutClass: function() {
      const count = this.componentData?.totalItems;
      return `owc-media-gallery__${count}-items`;
    },
    isDarkMode: function() {
      return Boolean(this.componentData?.darkMode);
    },
    pinchLabel: function() {
      return this.componentData?.labels?.overlayPinchLabel || '';
    },
    footnotes() {
      return getLocalFootnotes(this.componentData);
    },
    moduleId() {
      return setModuleId(this);
    },
  },
  methods: {
    async setItemHeight(tree: string[]) {
      const payload: Payload[] = [];
      if (!isItemHeightSet) {
        tree.forEach(id => {
          payload.push({
            id,
            json: {
              height: 'var(--mediaGalleryRowHeight)',
            },
          });
        });
        const connection = await getComponentStateConnection();
        isItemHeightSet = true;
        connection.updateComponents(payload);
      }
    },
    resizeEventHandler: function(): void {
      const mediaGalleryElement = this.$el as HTMLElement;
      const mediaGalleryContentWidth = mediaGalleryElement.clientWidth;

      // since for webkit 100vh is not the exact value as availble viewport
      // (the browser bar takes space from here) we'd need to use -webkit-fill-available
      // to get the exact value for 100vh. Still, this is not compatible with calc()
      // to work around this issue, we set a custom property for innerHeight that can then
      // be used for correct values in webkit
      mediaGalleryElement.style.setProperty('--window-height', `${window.innerHeight}px`);

      if (this.currentGridWidth !== mediaGalleryContentWidth) {
        mediaGalleryElement.style.setProperty('--mediaGalleryWidth', `${mediaGalleryContentWidth}px`);
      }
      this.currentGridWidth = mediaGalleryContentWidth;
    },
    openOverlay(screenMaxEvent: CustomEvent): void {
      this.isOverlayVisible = true;
      this.currentGalleryItemIndex = this.currentGalleryItems.findIndex(
        item => item.itemId === screenMaxEvent.detail.itemId,
      );
    },
    handleTracking(trackingEvent: CustomEvent): void {
      if (!this.isTrackingElementDispached) {
        for (let i = 0; i < this.currentGalleryItems.length; i++) {
          if (this.currentGalleryItems[i].itemId === trackingEvent.detail.itemId) {
            const mediaGalleryItem = this.currentGalleryItems[i];

            const trackingIndex = i + 1;
            const trackingContentType = mediaGalleryItem.json.videoDetails ? 'video' : 'image';
            const trackingButtonType = trackingEvent.detail.buttonType;

            const trackingLabel = `index${trackingIndex}.${trackingContentType}.${trackingButtonType}`;

            this.handleCustomTracking(this.$el, trackingLabel, 'feature', 'click');
            this.isTrackingElementDispached = true;
          }
        }
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/accessibility';
@import '@owc-styles/utils/rte';

// *
// * selectors
// *
$itemSelector: '.owc-media-gallery-item__container';

@mixin item($selector, $siblingSelector) {
  #{$selector}#{$siblingSelector},
  ::slotted(.webcomponent-nested#{$siblingSelector}) {
    @content;
  }
}
@mixin gallery {
  & > .webcomponent.webcomponent-simulator,
  slot {
    @content;
  }
}

// *
// * column mixins
// *
@mixin full {
  grid-column: 1 / -1;
}
@mixin left {
  grid-column: 1 / span 6;
}
@mixin right {
  grid-column: 7 / -1;
}
@mixin left-small {
  grid-column: 1 / span 4;
}
@mixin right-small {
  grid-column: 9 / -1;
}
@mixin left-large {
  grid-column: 1 / span 8;
}
@mixin right-large {
  grid-column: 5 / -1;
}
@mixin thirds($selector, $count) {
  $first: $count - 2;
  $second: $count - 1;

  @if ($count == 3) {
    @include item($selector, ':nth-of-type(1)') {
      grid-column: 1 / span 4;
    }
  }
  // prettier and stylelint do not agree on what to do with @else
  /* stylelint-disable-next-line at-rule-empty-line-before */
  @else {
    @include item($selector, ':nth-of-type(#{$first})') {
      grid-column: 1 / span 4;
    }
  }

  @include item($selector, ':nth-of-type(#{$second})') {
    grid-column: 5 / span 4;
  }
  @include item($selector, ':nth-of-type(#{$count})') {
    grid-column: 9 / -1;
  }
}
// *
// * layout mixins
// *
@mixin mediaGalleryLayoutMobile($selector, $count) {
  @if ($count == 2) {
    @include item($selector, ':nth-of-type(1)') {
      @include left;
    }
    @include item($selector, ':nth-of-type(2)') {
      @include right;
    }
  }
  @if ($count > 2) {
    @include item($selector, ':nth-of-type(1)') {
      @include full;
    }
    @include item($selector, ':nth-of-type(2)') {
      @include left;
    }
  }
  @if ($count >= 3) {
    @include item($selector, ':nth-of-type(3)') {
      @include right;
    }
  }
  @if ($count >= 4) {
    @include item($selector, ':nth-of-type(4)') {
      @include full;
    }
  }
  @if ($count == 5 or $count == 7) {
    @include item($selector, ':nth-of-type(5)') {
      @include full;
    }
  }
  @if ($count == 6) {
    @include item($selector, ':nth-of-type(5)') {
      @include left;
    }
    @include item($selector, ':nth-of-type(6)') {
      @include right;
    }
  }
  @if ($count > 6) {
    @include item($selector, ':nth-of-type(6)') {
      @include left;
    }
  }
  @if ($count >= 7) {
    @include item($selector, ':nth-of-type(7)') {
      @include right;
    }
  }
}
@mixin mediaGalleryLayoutDesktop($selector, $count) {
  @include item($selector, ':nth-of-type(1)') {
    @include left-large;
  }
  @include item($itemSelector, ':nth-of-type(2)') {
    @include right-small;
  }

  @if ($count == 3 or $count == 5 or $count == 7) {
    @include thirds($selector, $count);
  }
  @if ($count > 3 and $count != 5) {
    @include item($itemSelector, ':nth-of-type(3)') {
      @include left-small;
    }
  }
  @if ($count == 4 or $count >= 6) {
    @include item($itemSelector, ':nth-of-type(4)') {
      @include right-large;
    }
  }
  @if ($count == 6) {
    @include item($itemSelector, ':nth-of-type(5)') {
      @include left-large;
    }
    @include item($itemSelector, ':nth-of-type(6)') {
      @include right-small;
    }
  }
}
@mixin mediaGalleryRowsMobile($count, $mainImageHeight) {
  @if $count < 6 {
    grid-template-rows: repeat($count - 1, $mainImageHeight);
  }
  // prettier and stylelint do not agree on what to do with @else
  /* stylelint-disable-next-line at-rule-empty-line-before */
  @else {
    grid-template-rows: repeat($count - 2, $mainImageHeight);
  }
}
@mixin mediaGalleryRowsDesktop($count, $mainImageHeight) {
  @if $count < 4 {
    grid-template-rows: repeat(1, $mainImageHeight);
  }
  // prettier and stylelint do not agree on what to do with @else
  /* stylelint-disable-next-line at-rule-empty-line-before */
  @else if $count < 6 {
    grid-template-rows: repeat(2, $mainImageHeight);
  }
  // prettier and stylelint do not agree on what to do with @else
  /* stylelint-disable-next-line at-rule-empty-line-before */
  @else {
    grid-template-rows: repeat(3, $mainImageHeight);
  }
}

.owc-media-gallery {
  $mainContentWidth: var(--mediaGalleryWidth);
  $gridGutterSizes: calc(var(--wb-grid-gutter-width) * 11);
  $gridSigleColumnSize: calc(#{$mainContentWidth} - #{$gridGutterSizes}) / 12;
  $gutterWidths: calc(var(--wb-grid-gutter-width) * 7);
  $gridColumns: calc(#{$gridSigleColumnSize} * 8);
  $tileWidth: calc(#{$gridColumns} + #{$gutterWidths});
  $mainImageHeight: calc((9 * #{$tileWidth} / 16));

  --mediaGalleryRowHeight: #{$mainImageHeight};
  --mediaGalleryImagePosition: center;
  --mediaGalleryImageFit: cover;

  color: var(--wb-black);

  @include gallery {
    @include wb-grid-gaps;

    display: grid;
    grid-row-gap: var(--wb-grid-gutter-width);
    grid-template-columns: repeat(12, 1fr);
  }

  @for $i from 2 through 7 {
    &.owc-media-gallery__#{$i}-items {
      @include mediaGalleryLayoutMobile($itemSelector, $i);
      @include breakpoint-from(mq4) {
        @include mediaGalleryLayoutDesktop($itemSelector, $i);
      }
      @include gallery {
        @include mediaGalleryRowsMobile($i, $mainImageHeight);
        @include breakpoint-from(mq4) {
          @include mediaGalleryRowsDesktop($i, $mainImageHeight);
        }
      }
    }
  }

  &__theme-dark {
    color: var(--wb-white);

    background-color: var(--wb-black);
  }
  &__container {
    @include wb-content-width;

    padding-bottom: var(--wb-spacing-xl);
  }
  &__footnotes {
    margin-top: var(--wb-spacing-m);
  }
}
.owc-gallery-overlay-fade-in-out {
  $fade-in-out-transition-duration: 300ms;

  &-enter-active,
  &-leave-active {
    transition: opacity $wb-fade $fade-in-out-transition-duration;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
  }
  &-leave,
  &-enter-to {
    opacity: 1;
  }
}
</style>

<style lang="scss">
.owc-media-gallery_isEditMode {
  .owc-media-gallery {
    slot {
      display: flex;
      flex-direction: column;
    }
    ::slotted(.webcomponent-nested) {
      flex: 1;
      border: dashed 2px var(--wb-grey-70);
    }
  }
}
</style>
